import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import { useParams } from 'react-router-dom'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import { com, Nullable } from '@eidu/form'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import useEntityById from '../../io/useEntityById'
import { fieldValueToString } from '../../domain/entity/DraftFieldValue'
import { findAssetTypeByLabel, type RenderData } from './assetTypes'
import AssetRenderer from './AssetRenderer'
import EntityNotAccessibleError from '../entity/EntityNotAccessibleError'
import EntityTypeNotAccessibleError from '../entity/EntityTypeNotAccessibleError'
import { getTypeDisplayName } from '../../util/entityType/typeNameSchema'
import { createAuthContext } from '../../api/authorization/AuthenticationContext'
import apiBaseUrl from '../../api/baseUrl'
import entityApiUrl from '../../api/entity/baseUrl'
import { AssetEntityTypeConfig, useAssetTypeRegistry } from './useAssetTypeRegistry'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import FieldValue = com.eidu.sharedlib.entity.field.FieldValue
import ReferenceValue = com.eidu.sharedlib.entity.field.ReferenceValue
import entityIdFromStringOrNull = com.eidu.sharedlib.entity.entityIdFromStringOrNull

const normalize = (value: string) => value.trim().toLowerCase()

const findFieldByName = (entityType: EntityType, name: string) =>
  entityType.fields.asJsReadonlyArrayView().find((field) => normalize(field.name) === normalize(name))

const getFieldString = (values: ReadonlyMap<FieldId, Nullable<FieldValue>>, fieldId?: FieldId) =>
  fieldId ? fieldValueToString(values.get(fieldId) ?? null).trim() : ''

const getAssetTypeLabel = (asset: EntityWithLabelAndRelated, typeFieldId?: FieldId) => {
  if (!typeFieldId) return undefined
  const values = asset.entity.valuesByFieldId.asJsReadonlyMapView()
  const typeValue = values.get(typeFieldId) ?? null
  if (typeValue instanceof ReferenceValue) {
    const referenced = asset.getReferencedEntity(typeValue.value)
    return referenced?.primaryLabelText?.trim()
  }
  return typeValue ? fieldValueToString(typeValue).trim() : undefined
}

const resolveAssetTypeForEntity = (asset: EntityWithLabelAndRelated, assetConfig?: AssetEntityTypeConfig) => {
  if (!assetConfig) return undefined
  if (assetConfig.allowedTypes.length === 1 && !assetConfig.typeFieldId) return assetConfig.allowedTypes[0]
  const typeLabel = getAssetTypeLabel(asset, assetConfig.typeFieldId)
  if (!typeLabel) return assetConfig.allowedTypes.length === 1 ? assetConfig.allowedTypes[0] : undefined
  const resolved = findAssetTypeByLabel(typeLabel)
  if (!resolved) return undefined
  return assetConfig.allowedTypeIds.includes(resolved.id) ? resolved : undefined
}

const resolveFieldTypeName = (fieldType: FieldType) => {
  if (typeof fieldType === 'string') return fieldType
  if (typeof fieldType === 'number') {
    return (FieldType as unknown as Record<number, string>)[fieldType] ?? String(fieldType)
  }
  return String(fieldType)
}

const PAGE_SPINNER_DELAY_MS = 1000
const PAGE_SPINNER_SIZE_PX = 28
const TERMINAL_ERROR_STABILIZATION_MS = 2000

const AssetRoute = () => {
  const params = useParams()
  const assetId = params.assetId ? entityIdFromStringOrNull(params.assetId) ?? undefined : undefined

  const entityTypesRepo = useContext(EntityTypesContext)
  const entityTypes = entityTypesRepo.useAll()
  const entityTypesLoading = entityTypesRepo.useLoading()

  const { entity, loading: entityLoading } = useEntityById(assetId)
  const iAuthContext = useContext<IAuthContext>(AuthContext)
  const authContext = useMemo(
    () => createAuthContext(iAuthContext),
    [iAuthContext.token, iAuthContext.idTokenData?.organization_id, iAuthContext.idTokenData?.user_entity_id]
  )

  const { assetEntityTypesById, loading: assetRegistryLoading } = useAssetTypeRegistry(entityTypes ?? undefined)

  const entityType = useMemo(() => {
    if (!entity || !entityTypes) return undefined
    return entityTypes.get(entity.entity.typeId) ?? undefined
  }, [entity, entityTypes])

  const assetConfig = useMemo(
    () => (entityType ? assetEntityTypesById.get(entityType.id) : undefined),
    [assetEntityTypesById, entityType]
  )

  const assetType = useMemo(
    () => (entity && assetConfig ? resolveAssetTypeForEntity(entity, assetConfig) : undefined),
    [assetConfig, entity]
  )

  const payload = useMemo(() => {
    if (!entity || !assetConfig) return ''
    const values = entity.entity.valuesByFieldId.asJsReadonlyMapView()
    return fieldValueToString(values.get(assetConfig.payloadFieldId) ?? null)
  }, [assetConfig, entity])

  const title = useMemo(() => {
    if (!entity || !entityType) return 'Asset'
    const nameField = findFieldByName(entityType, 'Name')
    if (nameField) {
      const values = entity.entity.valuesByFieldId.asJsReadonlyMapView()
      const nameValue = getFieldString(values, nameField.id)
      if (nameValue) return nameValue
    }
    return entity.primaryLabelText ?? 'Asset'
  }, [entity, entityType])

  const renderData = useMemo<RenderData | undefined>(() => {
    if (!entity || !entityType) return undefined
    const values = entity.entity.valuesByFieldId.asJsReadonlyMapView()
    const entitySnapshot = {
      id: entity.entity.id.toString(),
      typeId: entityType.id.toString(),
      typeName: getTypeDisplayName(entityType.name),
      fields: entityType.fields.asJsReadonlyArrayView().map((field) => ({
        id: field.id.toString(),
        name: field.name,
        type: resolveFieldTypeName(field.type),
        value: fieldValueToString(values.get(field.id) ?? null),
        required: field.required,
      })),
    }
    const auth =
      authContext &&
      ({
        accessToken: authContext.accessToken,
        organizationId: authContext.organizationId,
        apiBaseUrl,
        entityApiUrl,
        userEntityId: authContext.userEntityId,
      } as const)
    return { entity: entitySnapshot, auth: auth ?? undefined, context: { embedDepth: 0 } }
  }, [authContext, entity, entityType])

  const entityResolved = entity !== undefined || !assetId
  const entityTypesResolved = entityTypes !== undefined
  const isBootstrapping =
    !entityResolved || !entityTypesResolved || entityLoading || entityTypesLoading || assetRegistryLoading
  const hasRenderableAsset = Boolean(entity && entityType && assetConfig && assetType)
  const hasTerminalResolutionError = !isBootstrapping && !hasRenderableAsset
  const [showBootSpinner, setShowBootSpinner] = useState(false)
  const [showTerminalError, setShowTerminalError] = useState(false)
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false)

  useEffect(() => {
    if (!isBootstrapping) {
      setShowBootSpinner(false)
      return () => undefined
    }
    const timeoutId = window.setTimeout(() => setShowBootSpinner(true), PAGE_SPINNER_DELAY_MS)
    return () => window.clearTimeout(timeoutId)
  }, [isBootstrapping])

  useEffect(() => {
    if (!hasTerminalResolutionError) {
      setShowTerminalError(false)
      return () => undefined
    }
    const timeoutId = window.setTimeout(() => setShowTerminalError(true), TERMINAL_ERROR_STABILIZATION_MS)
    return () => window.clearTimeout(timeoutId)
  }, [hasTerminalResolutionError])

  const openPreviewFullscreen = useCallback(async () => {
    setIsPreviewFullscreen(true)
    if (typeof document === 'undefined') return
    if (!document.documentElement.requestFullscreen) return
    try {
      await document.documentElement.requestFullscreen()
    } catch {
      // Ignore fullscreen errors and fall back to the overlay.
    }
  }, [])

  const closePreviewFullscreen = useCallback(() => {
    setIsPreviewFullscreen(false)
    if (typeof document === 'undefined') return
    if (!document.fullscreenElement) return
    if (document.exitFullscreen) {
      document.exitFullscreen().catch(() => undefined)
    }
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsPreviewFullscreen(false)
      }
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  useEffect(() => {
    if (!isPreviewFullscreen) return undefined
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      closePreviewFullscreen()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [closePreviewFullscreen, isPreviewFullscreen])

  useEffect(() => {
    if (!isPreviewFullscreen || typeof document === 'undefined') return undefined
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isPreviewFullscreen])

  const renderAsset = () =>
    hasRenderableAsset ? (
      <AssetRenderer content={payload} assetType={assetType} renderData={renderData} layout="fill" showLog={false} />
    ) : null

  const fullscreenOverlay =
    isPreviewFullscreen && typeof document !== 'undefined'
      ? createPortal(
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: (theme) => theme.zIndex.modal + 10,
              bgcolor: 'background.default',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <IconButton
              aria-label="Close fullscreen preview"
              onClick={closePreviewFullscreen}
              sx={{
                position: 'absolute',
                top: 'calc(16px + env(safe-area-inset-top))',
                right: 'calc(16px + env(safe-area-inset-right))',
                zIndex: 2,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: '#fff',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.8)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                minHeight: 0,
              }}
            >
              {renderAsset()}
            </Box>
          </Box>,
          document.body
        )
      : null

  return (
    <>
      {isBootstrapping && (
        <Stack sx={{ height: '100%', minHeight: 0, alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
          {showBootSpinner && <CircularProgress color="primary" size={PAGE_SPINNER_SIZE_PX} />}
        </Stack>
      )}
      {!isBootstrapping && hasTerminalResolutionError && !showTerminalError && (
        <Stack sx={{ height: '100%', minHeight: 0, alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
          <CircularProgress color="primary" size={PAGE_SPINNER_SIZE_PX} />
        </Stack>
      )}
      {hasRenderableAsset && (
        <Stack sx={{ height: '100%', minHeight: 0 }}>
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 2,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography variant="h4">{title}</Typography>
            <Button color="primary" startIcon={<FullscreenIcon />} onClick={openPreviewFullscreen}>
              Full Screen
            </Button>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>{renderAsset()}</Box>
        </Stack>
      )}
      {hasTerminalResolutionError && showTerminalError && !entity && <EntityNotAccessibleError />}
      {hasTerminalResolutionError && showTerminalError && entity && !entityType && <EntityTypeNotAccessibleError />}
      {hasTerminalResolutionError && showTerminalError && entity && entityType && (!assetConfig || !assetType) && (
        <EntityNotAccessibleError />
      )}
      {fullscreenOverlay}
    </>
  )
}

export default AssetRoute
